"use client"

import { useEffect, useState } from "react"

interface PinToggleProps {
  pinned: boolean
  onToggle: () => void
  // Layout mode:
  //   "overlay" — absolutely positioned at the top-right of the nearest
  //     positioned ancestor. Pair with a `relative group` parent that
  //     contains the artifact being pinned.
  //   "free" — no positioning classes; the caller wraps the button in
  //     whatever container fits (e.g. ZoomableViewer's fixed top-left slot).
  // Default "overlay".
  layout?: "overlay" | "free"
  // When true, the button is always visible. When false (default for
  // overlay layout), the button hover-reveals at wide viewports and is
  // always visible at narrow ones.
  alwaysVisible?: boolean
  ariaLabelPin?: string
  ariaLabelUnpin?: string
  className?: string
}

// PinToggle — a quilting-pin-shaped toggle button. Renders a glass-head
// pin (slanted shaft, colored bead) that fills with color when pinned and
// plays a brief "thwack" bounce on click. Stops event propagation so the
// underlying clickable (e.g. fullscreen viewer trigger) doesn't fire.
export default function PinToggle({
  pinned,
  onToggle,
  layout = "overlay",
  alwaysVisible = false,
  ariaLabelPin = "Pin to scrapbook",
  ariaLabelUnpin = "Unpin from scrapbook",
  className = "",
}: PinToggleProps) {
  const [bumping, setBumping] = useState(false)

  useEffect(() => {
    if (!bumping) return
    const t = setTimeout(() => setBumping(false), 450)
    return () => clearTimeout(t)
  }, [bumping])

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    setBumping(true)
    onToggle()
  }

  const layoutClass = layout === "overlay"
    ? "absolute top-1.5 right-1.5 z-10"
    : ""

  const visibilityClass = alwaysVisible || layout === "free"
    ? "opacity-100"
    : "opacity-100 lg:opacity-0 lg:group-hover:opacity-100 focus-visible:opacity-100"

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={pinned ? ariaLabelUnpin : ariaLabelPin}
      aria-pressed={pinned}
      className={`p-1 rounded-full
        ${layoutClass}
        ${visibilityClass}
        transition-opacity duration-150
        focus:outline-none focus-visible:ring-2 focus-visible:ring-ring
        cursor-pointer
        ${className}`}
    >
      <span className={`block ${bumping ? "animate-pin-thwack" : ""}`}>
        <PinIcon pinned={pinned} />
      </span>
    </button>
  )
}

function PinIcon({ pinned }: { pinned: boolean }) {
  return (
    <svg
      width="26"
      height="26"
      viewBox="0 0 32 32"
      aria-hidden="true"
      className="block"
      style={{
        // Tilt — pin sits at a natural quilter's-angle. Slight rotation
        // change between states so unpinned pins read as "lying loose"
        // and pinned ones as "stabbed in."
        transform: pinned ? "rotate(-12deg)" : "rotate(-26deg)",
        transition: "transform 200ms cubic-bezier(0.34, 1.56, 0.64, 1)",
        filter: pinned
          ? "drop-shadow(0 1.5px 2px rgba(0,0,0,0.35))"
          : "drop-shadow(0 1px 1.2px rgba(0,0,0,0.18))",
      }}
    >
      {/* Shaft (slanted from bead at top-left to point at bottom-right) */}
      <line
        x1="11" y1="11" x2="26" y2="26"
        stroke="#777"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      {/* Shaft highlight along its length — gives a metallic sheen */}
      <line
        x1="12" y1="11" x2="25" y2="24"
        stroke="white"
        strokeWidth="0.5"
        strokeLinecap="round"
        opacity="0.55"
      />
      {/* Sharp point */}
      <circle cx="26" cy="26" r="0.7" fill="#555" />
      {/* Glass bead head */}
      <circle
        cx="9.5" cy="9.5" r="6.2"
        fill={pinned ? "#dc4747" : "#fff"}
        stroke={pinned ? "#9a2a2a" : "#888"}
        strokeWidth="1.2"
      />
      {/* Inner bead — slightly darker rim creates depth */}
      <circle
        cx="9.5" cy="9.5" r="5"
        fill="none"
        stroke={pinned ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.06)"}
        strokeWidth="1"
      />
      {/* Glass highlight — top-left lozenge */}
      <ellipse
        cx="7" cy="7" rx="2.4" ry="1.5"
        fill="white"
        opacity={pinned ? 0.55 : 0.85}
        transform="rotate(-30 7 7)"
      />
      {/* Tiny secondary highlight — completes the "glass bead" reading */}
      <circle
        cx="11.5" cy="11.5" r="0.7"
        fill="white"
        opacity={pinned ? 0.5 : 0.7}
      />
    </svg>
  )
}
